import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import useNotifications from "@/hooks/useNotifications";
import { Eye, Receipt, Camera, Bell, CheckCircle, Circle, ArrowRight } from "@phosphor-icons/react";
import WelcomeQuickStart from "@/components/WelcomeQuickStart";

/**
 * Tier 1 (Field Operator) landing page.
 * 4 sections: today's tasks · big action buttons · my recent sightings · last 5 notifications.
 * Mobile-first, 48px tap targets, no sidebar (Layout already swaps to bottom-nav on mobile for Tier 1).
 */
export default function MyTasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [sightings, setSightings] = useState([]);
  const notifs = useNotifications();

  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!user?.user_id) return;
    let cancelled = false;
    (async () => {
      try {
        const [tk, sg] = await Promise.all([
          api.get("/field-tasks"),
          api.get("/sighting-reports").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const mine = (tk.data || []).filter(t =>
          t.assigned_to === user?.name ||
          t.assigned_to === user?.initials ||
          t.assigned_to === user?.email
        );
        setTasks(mine.slice(0, 20));
        const mySightings = (sg.data || []).filter(s =>
          s.submitted_by_user_id === user?.user_id ||
          s.submitted_by === user?.name ||
          s.submitted_by_initials === user?.initials
        );
        setSightings(mySightings.slice(0, 5));
      } catch (e) { /* swallow */ }
    })();
    return () => { cancelled = true; };
  }, [user?.user_id, user?.name, user?.initials, user?.email, refreshKey]);

  const completeTask = async (taskId) => {
    await api.post(`/field-tasks/${taskId}/complete`);
    setRefreshKey(k => k + 1);
  };

  const todayStr = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="space-y-6 max-w-3xl" data-testid="my-tasks-page">
      {/* Header */}
      <div>
        <div className="label-eyebrow">Field Operator</div>
        <h1 className="font-display text-[28px] font-bold tracking-tight mt-1" data-testid="my-tasks-greeting">
          {greet}, {user?.name?.split(" ")[0] || user?.initials || "Field Agent"}
        </h1>
        <div className="text-[12px] text-[var(--span-text-2)] mt-1 font-mono">{todayStr}</div>
      </div>

      {/* First-visit Welcome / Quick-Start tile (auto-hides after dismiss or ≥3 sightings filed) */}
      <WelcomeQuickStart taskCount={tasks.length} sightingCount={sightings.length} variant="ops" />

      {/* Push notification opt-in (Tier 1 mobile-first) */}
      {notifs.permission === "default" && (
        <div className="card-flat p-4 flex items-start gap-3 border-l-2 border-[var(--span-accent,#004EEB)]" data-testid="push-opt-in-banner">
          <Bell size={18} weight="fill" className="mt-0.5 text-[var(--span-accent,#004EEB)] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-[13px]">Get alerts the second they happen</div>
            <div className="text-[11px] text-[var(--span-text-2)] mt-0.5">
              Allow push notifications so new tasks, sightings and escalations buzz your phone immediately — even when SPAN isn&apos;t open.
            </div>
          </div>
          <button
            data-testid="push-opt-in-btn"
            onClick={() => notifs.requestPermission()}
            className="span-btn h-9 text-[12px] shrink-0"
          >
            Turn on
          </button>
        </div>
      )}
      {notifs.permission === "granted" && (
        <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--span-success)]" data-testid="push-status-granted">
          Push notifications · ON
        </div>
      )}

      {/* Big action buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Link
          to="/reports/sighting/new"
          data-testid="my-tasks-file-sighting"
          className="card-flat p-5 flex items-center gap-4 min-h-[80px] hover:bg-[var(--span-bg-2)] transition-colors active:scale-[0.99]"
        >
          <div className="w-12 h-12 bg-black flex items-center justify-center shrink-0">
            <Eye size={22} weight="regular" color="white" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-[16px]">File a sighting</div>
            <div className="text-[11px] text-[var(--span-text-2)] mt-0.5 font-mono uppercase tracking-[0.12em]">Surveillance · Test purchase · Raid</div>
          </div>
          <ArrowRight size={16} />
        </Link>
        <Link
          to="/expenses/new"
          data-testid="my-tasks-submit-expense"
          className="card-flat p-5 flex items-center gap-4 min-h-[80px] hover:bg-[var(--span-bg-2)] transition-colors active:scale-[0.99]"
        >
          <div className="w-12 h-12 bg-[var(--span-accent,#004EEB)] flex items-center justify-center shrink-0">
            <Receipt size={22} weight="regular" color="white" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-[16px]">Submit an expense</div>
            <div className="text-[11px] text-[var(--span-text-2)] mt-0.5 font-mono uppercase tracking-[0.12em]">Travel · Per-diem · Receipts</div>
          </div>
          <ArrowRight size={16} />
        </Link>
      </div>

      {/* Today's tasks */}
      <section data-testid="my-tasks-list">
        <h2 className="label-eyebrow mb-3">Your tasks today</h2>
        <div className="card-flat">
          {tasks.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <CheckCircle size={32} weight="thin" className="mx-auto mb-2 text-[var(--span-text-2)]" />
              <div className="text-[13px] font-medium">All caught up</div>
              <div className="text-[11px] text-[var(--span-text-2)] mt-1 font-mono">No field tasks assigned for today.</div>
            </div>
          ) : (
            <ul className="divide-y divide-[var(--span-border)]">
              {tasks.map((t, i) => (
                <li key={t.task_id || `task-${i}`} className="px-5 py-3 flex items-start gap-3 min-h-[56px]" data-testid={`task-row-${t.task_id || i}`}>
                  <button
                    onClick={() => completeTask(t.task_id)}
                    disabled={t.status === "completed"}
                    data-testid={`task-complete-${t.task_id}`}
                    className="mt-1 shrink-0"
                    aria-label="Mark task complete"
                  >
                    {t.status === "completed"
                      ? <CheckCircle size={20} weight="fill" className="text-[var(--span-success)]" />
                      : <Circle size={20} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--span-text-2)]">
                      {t.case_id ? t.case_id.slice(0, 12) + "…" : "STANDALONE"}
                    </div>
                    <div className="font-medium text-[13px] mt-0.5">{t.title}</div>
                    <div className="text-[11px] text-[var(--span-text-2)] font-mono mt-0.5">
                      {t.location} · {t.priority}
                    </div>
                  </div>
                  <span className={`badge text-[10px] ${t.status === "completed" ? "badge-success" : t.status === "in_progress" ? "badge-warn" : "badge-info"}`}>
                    {t.status || "assigned"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* My recent sightings */}
      <section data-testid="my-tasks-sightings">
        <h2 className="label-eyebrow mb-3">My recent sightings</h2>
        <div className="card-flat">
          {sightings.length === 0 ? (
            <div className="px-5 py-6 text-center text-[12px] text-[var(--span-text-2)]">No sightings filed yet.</div>
          ) : (
            <ul className="divide-y divide-[var(--span-border)]">
              {sightings.map((s, i) => (
                <li key={s.report_id || s.sighting_id || `sighting-${i}`} className="px-5 py-3 flex items-center gap-3" data-testid={`sighting-row-${s.report_id || s.sighting_id || i}`}>
                  <Camera size={16} className="shrink-0 text-[var(--span-text-2)]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium truncate">{s.title || s.summary?.slice(0, 60) || "Sighting"}</div>
                    <div className="text-[10px] text-[var(--span-text-2)] font-mono mt-0.5">
                      {fmtDate(s.submitted_at || s.created_at)} · {s.location || s.city || "—"}
                    </div>
                  </div>
                  <span className={`badge text-[10px] ${s.status === "under_review" ? "badge-warn" : s.status === "cover_note_sent" ? "badge-success" : "badge-info"}`}>
                    {s.status || "submitted"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Notifications */}
      <section data-testid="my-tasks-notifs">
        <h2 className="label-eyebrow mb-3 flex items-center justify-between">
          <span>Notifications</span>
          {notifs.unread > 0 && (
            <span className="font-mono text-[10px] text-[var(--span-danger)]">{notifs.unread} unread</span>
          )}
        </h2>
        <div className="card-flat">
          {(!notifs.items || notifs.items.length === 0) ? (
            <div className="px-5 py-6 text-center text-[12px] text-[var(--span-text-2)]">No notifications.</div>
          ) : (
            <ul className="divide-y divide-[var(--span-border)]">
              {notifs.items.slice(0, 5).map((n, i) => (
                <li key={n.notif_id || n.id || `notif-${i}`} className="px-5 py-3 flex items-start gap-3" data-testid={`notif-row-${n.notif_id || n.id || i}`}>
                  <Bell size={14} weight={n.read ? "regular" : "fill"} className="mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className={`text-[13px] ${n.read ? "text-[var(--span-text-2)]" : "font-medium"}`}>{n.title || n.body || "Notification"}</div>
                    <div className="text-[10px] text-[var(--span-text-2)] font-mono mt-0.5">{fmtDate(n.created_at)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
